import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { RouteError } from "@/components/RouteError";
import { PresetEditorScreen } from "@/features/preset-editor/EditorScreen";
import { editingQuery } from "@/features/preset-editor/queries";
import type { Viewing } from "@/features/preset-editor/versions";
import { isNotFound } from "@/lib/api";

interface EditorSearch {
  draft?: string;
  version?: string;
}

export const Route = createFileRoute("/_authenticated/presets/$slug/edit")({
  validateSearch: (search: Record<string, unknown>): EditorSearch => ({
    ...(typeof search.draft === "string" ? { draft: search.draft } : {}),
    ...(typeof search.version === "string" ? { version: search.version } : {}),
  }),
  staticData: { access: "account" },
  loader: ({ context: { queryClient }, params: { slug } }) =>
    queryClient.ensureQueryData(editingQuery(slug)),
  head: () => ({ meta: [{ title: "Edit preset · Pax.co" }] }),
  errorComponent: props => (isNotFound(props.error) ? <NotOnTeam /> : <RouteError {...props} />),
  pendingComponent: EditorPending,
  component: EditorPage,
});

function EditorPage() {
  const { slug } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data: editing } = useSuspenseQuery(editingQuery(slug));
  const asked: Viewing | null = search.draft
    ? { kind: "draft", id: search.draft }
    : search.version
      ? { kind: "version", id: search.version }
      : null;
  return (
    <PresetEditorScreen
      key={slug}
      editing={editing}
      asked={asked}
      onView={viewing =>
        navigate({
          search: viewing.kind === "draft" ? { draft: viewing.id } : { version: viewing.id },
          replace: true,
        })
      }
    />
  );
}

function NotOnTeam() {
  return (
    <div className="grid h-full place-items-center p-8 text-center">
      <div>
        <p className="text-base font-medium">You aren't on this preset's team</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Its team can invite you. Until then, you can play it or make a copy of your own.
        </p>
        <Link to="/" className="mt-4 inline-block text-sm font-medium underline">
          Back to the store
        </Link>
      </div>
    </div>
  );
}

function EditorPending() {
  return (
    <div aria-busy className="flex h-full flex-col">
      <div className="h-12 shrink-0" />
      <div className="mx-auto h-8 w-96 max-w-full animate-pulse rounded-full bg-foreground/5" />
      <div className="mt-4 min-h-0 flex-1 animate-pulse rounded-t-2xl bg-foreground/[0.03]" />
    </div>
  );
}
